import type { ComponentType } from 'react'
import type { SvgIconProps, Theme } from '@mui/material'
import {
  ArrowBack,
  ArrowForward,
  ChangeHistory,
  CheckBoxOutlineBlank,
  RadioButtonUnchecked,
} from '@mui/icons-material'

export enum EventType {
  USER_PROMPT,
  AGENT_REPLY,
  AGENT_NARRATIVE,
  AGENT_QUESTIONS,
  PLAN_APPROVAL_REQUEST,
  TOOL_PERMISSION_REQUEST,
  FILE_MODIFICATION,
  ERRORED_EVENT,
  NON_INTERACTIVE_NOTIFICATION,
  PRE_TOOL_USE_ACTIVITY,
  POST_TOOL_USE_ACTIVITY,
}

export function eventTypeFrom(value: string): EventType {
  switch (value) {
    case 'USER_PROMPT':
      return EventType.USER_PROMPT
    case 'AGENT_REPLY':
      return EventType.AGENT_REPLY
    case 'AGENT_NARRATIVE':
      return EventType.AGENT_NARRATIVE
    case 'AGENT_QUESTIONS':
      return EventType.AGENT_QUESTIONS
    case 'PLAN_APPROVAL_REQUEST':
      return EventType.PLAN_APPROVAL_REQUEST
    case 'TOOL_PERMISSION_REQUEST':
      return EventType.TOOL_PERMISSION_REQUEST
    case 'FILE_MODIFICATION':
      return EventType.FILE_MODIFICATION
    case 'ERRORED_EVENT':
      return EventType.ERRORED_EVENT
    case 'NON_INTERACTIVE_NOTIFICATION':
      return EventType.NON_INTERACTIVE_NOTIFICATION
    case 'PRE_TOOL_USE_ACTIVITY':
      return EventType.PRE_TOOL_USE_ACTIVITY
    case 'POST_TOOL_USE_ACTIVITY':
      return EventType.POST_TOOL_USE_ACTIVITY
    default:
      throw new Error(`Unknown event type: ${value}`)
  }
}

export function resolveEventTypeLabelFor(eventType: EventType): string {
  const labels: Record<EventType, string> = {
    [EventType.USER_PROMPT]: 'User Prompt',
    [EventType.AGENT_REPLY]: 'Agent Reply',
    [EventType.AGENT_NARRATIVE]: 'Agent Narrative',
    [EventType.AGENT_QUESTIONS]: 'Agent Questions',
    [EventType.PLAN_APPROVAL_REQUEST]: 'Plan Approval Request',
    [EventType.TOOL_PERMISSION_REQUEST]: 'Tool Permission Request',
    [EventType.FILE_MODIFICATION]: 'File Modification',
    [EventType.ERRORED_EVENT]: 'Errored Event',
    [EventType.NON_INTERACTIVE_NOTIFICATION]: 'Non-interactive notification',
    [EventType.PRE_TOOL_USE_ACTIVITY]: 'Pre Tool Use Activity',
    [EventType.POST_TOOL_USE_ACTIVITY]: 'Post Tool Use Activity',
  }

  return labels[eventType]
}

export function eventTypeToString(type: EventType): string {
  switch (type) {
    case EventType.USER_PROMPT:
      return 'USER_PROMPT'
    case EventType.AGENT_REPLY:
      return 'AGENT_REPLY'
    case EventType.AGENT_NARRATIVE:
      return 'AGENT_NARRATIVE'
    case EventType.AGENT_QUESTIONS:
      return 'AGENT_QUESTIONS'
    case EventType.PLAN_APPROVAL_REQUEST:
      return 'PLAN_APPROVAL_REQUEST'
    case EventType.TOOL_PERMISSION_REQUEST:
      return 'TOOL_PERMISSION_REQUEST'
    case EventType.FILE_MODIFICATION:
      return 'FILE_MODIFICATION'
    case EventType.ERRORED_EVENT:
      return 'ERRORED_EVENT'
    case EventType.NON_INTERACTIVE_NOTIFICATION:
      return 'NON_INTERACTIVE_NOTIFICATION'
    case EventType.PRE_TOOL_USE_ACTIVITY:
      return 'PRE_TOOL_USE_ACTIVITY'
    case EventType.POST_TOOL_USE_ACTIVITY:
      return 'POST_TOOL_USE_ACTIVITY'
    default:
      return `UNKNOWN(${type})`
  }
}

export function resolveEventTypeColorFor(eventType: EventType, theme: Theme): string {
  const isDark = theme.palette.mode === 'dark'

  const colors: Record<EventType, string> = {
    [EventType.USER_PROMPT]: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
    [EventType.AGENT_REPLY]: isDark ? theme.palette.success.main : theme.palette.success.dark,
    [EventType.AGENT_NARRATIVE]: theme.palette.primary.main,
    [EventType.AGENT_QUESTIONS]: isDark ? theme.palette.warning.main : theme.palette.warning.dark,
    [EventType.PLAN_APPROVAL_REQUEST]: isDark ? '#00acc1' : theme.palette.info.main,
    [EventType.TOOL_PERMISSION_REQUEST]: isDark
      ? theme.palette.warning.main
      : theme.palette.warning.dark,
    [EventType.FILE_MODIFICATION]: isDark
      ? theme.palette.secondary.main
      : theme.palette.primary.dark,
    [EventType.ERRORED_EVENT]: isDark ? theme.palette.error.main : theme.palette.error.dark,
    [EventType.NON_INTERACTIVE_NOTIFICATION]: isDark
      ? theme.palette.warning.main
      : theme.palette.warning.dark,
    [EventType.PRE_TOOL_USE_ACTIVITY]: isDark ? theme.palette.info.main : theme.palette.info.dark,
    [EventType.POST_TOOL_USE_ACTIVITY]: isDark ? theme.palette.info.main : theme.palette.info.dark,
  }

  return colors[eventType]
}

export function resolveEventTypeIconFor(eventType: EventType): ComponentType<SvgIconProps> | null {
  const icons: Record<EventType, ComponentType<SvgIconProps> | null> = {
    [EventType.USER_PROMPT]: ArrowForward,
    [EventType.AGENT_REPLY]: ArrowBack,
    [EventType.AGENT_NARRATIVE]: ArrowForward,
    [EventType.AGENT_QUESTIONS]: RadioButtonUnchecked,
    [EventType.PLAN_APPROVAL_REQUEST]: CheckBoxOutlineBlank,
    [EventType.TOOL_PERMISSION_REQUEST]: ChangeHistory,
    [EventType.FILE_MODIFICATION]: ArrowForward,
    [EventType.ERRORED_EVENT]: ChangeHistory,
    [EventType.NON_INTERACTIVE_NOTIFICATION]: null,
    [EventType.PRE_TOOL_USE_ACTIVITY]: null,
    [EventType.POST_TOOL_USE_ACTIVITY]: null,
  }

  return icons[eventType]
}

export function shouldRotateIcon(eventType: EventType): boolean {
  return eventType === EventType.ERRORED_EVENT
}

export function getIconRotation(eventType: EventType): number {
  if (eventType === EventType.ERRORED_EVENT) {
    return 180
  }
  return 0
}
